<template>
    <div class="ui-page">
        <ui-page-header title="E-mails" subtitle="E-mails enviados pelo sistema, com a situação de entrega de cada um.">
            <v-btn id="btnExport" type="button" color="primary" outlined :loading="exportLoading" @click="exportXlsx">
                <v-icon left small>mdi-file-excel-outline</v-icon>
                Exportar Excel
            </v-btn>
        </ui-page-header>

        <!-- Aviso de falhas recentes: quem nao recebeu aparece aqui sem precisar procurar -->
        <v-alert v-if="alert.failures > 0" dense text type="warning" class="mb-4 d-flex align-center">
            <div class="d-flex align-center justify-space-between flex-wrap" style="width: 100%; gap: 8px;">
                <span>
                    <strong>{{ alert.failures }}</strong> e-mail(s) não chegaram ao destinatário nos últimos
                    {{ alert.days }} dias. Verifique se o endereço está correto no cadastro.
                </span>
                <v-btn small text color="warning darken-2" @click="showOnlyFailures">
                    Ver os que falharam
                </v-btn>
            </div>
        </v-alert>

        <!-- Totais do filtro atual -->
        <v-row class="mb-2">
            <v-col v-for="card in summaryCards" :key="card.key" cols="6" md="3">
                <v-card flat class="summary-card" :class="{ 'summary-card--active': dataList.filter.status === card.key }"
                    @click="toggleStatus(card.key)">
                    <div class="summary-value" :class="card.color + '--text'">{{ card.value }}</div>
                    <div class="summary-label ui-muted">{{ card.label }}</div>
                </v-card>
            </v-col>
        </v-row>

        <ui-table-card :more-count="moreFiltersCount" clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field dense placeholder="Destinatário, nome ou assunto" v-model="dataList.filter.term"
                    hide-details outlined clearable prepend-inner-icon="mdi-magnify" class="ui-toolbar__search"
                    @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-autocomplete dense :items="dataset.clients" placeholder="Todos os casos" class="ui-toolbar__field"
                    v-model="dataList.filter.client_id" item-value="id" item-text="name" :menu-props="{ offsetY: true }"
                    outlined hide-details clearable no-data-text="Nenhum caso encontrado" @change="applyFilters">
                </v-autocomplete>
                <v-select dense :items="dataset.kinds" placeholder="Todos os tipos de e-mail" class="ui-toolbar__field"
                    v-model="dataList.filter.kind" item-value="id" item-text="label" :menu-props="{ offsetY: true }"
                    outlined hide-details clearable @change="applyFilters"></v-select>
                <v-select dense :items="dataset.statuses" placeholder="Todas as situações" class="ui-toolbar__field"
                    v-model="dataList.filter.status" item-value="id" item-text="label" :menu-props="{ offsetY: true }"
                    outlined hide-details clearable @change="applyFilters"></v-select>
            </template>
            <template #more>
                <v-text-field dense type="date" label="De" v-model="dataList.filter.start_date" class="ui-toolbar__field"
                    outlined hide-details clearable @change="applyFilters"></v-text-field>
                <v-text-field dense type="date" label="Até" v-model="dataList.filter.end_date" class="ui-toolbar__field"
                    outlined hide-details clearable @change="applyFilters"></v-text-field>
            </template>

            <v-data-table :headers="dataList.headers" :items="dataList.data" :loading="dataList.loading"
                :server-items-length="dataList.count" :options.sync="dataList.options"
                disable-sort hide-default-footer class="ui-clickable-rows"
                no-data-text="Nenhum e-mail encontrado" loading-text="Carregando e-mails..."
                @click:row="openDetail">

                <template v-slot:[`item.recipient`]="{ item }">
                    <div class="ui-cell-main">{{ item.recipient_name || '—' }}</div>
                    <div class="ui-cell-sub">{{ item.recipient }}</div>
                </template>

                <template v-slot:[`item.subject`]="{ item }">
                    <div>{{ item.subject || '—' }}</div>
                    <div class="ui-cell-sub">{{ item.kind_label }}</div>
                </template>

                <template v-slot:[`item.client`]="{ item }">
                    {{ item.client ? item.client.name : '—' }}
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="item.status_label" :color="statusColor(item.status)" />
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Ver comprovante', icon: 'mdi-eye-outline', handler: () => openDetail(item) },
                        { text: 'Baixar comprovante em PDF', icon: 'mdi-file-pdf-box', handler: () => downloadReceipt(item) },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                    :items-per-page-options="dataList.footerOptions.itemsPerPageOptions"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>

        <!-- Comprovante -->
        <v-dialog v-model="detail.dialog" max-width="820px" scrollable>
            <v-card v-if="detail.item">
                <v-card-title class="d-flex align-center">
                    <v-icon left :color="statusColor(detail.item.status)">{{ statusIcon(detail.item.status) }}</v-icon>
                    Comprovante de envio
                    <ui-badge class="ml-3" :text="detail.item.status_label" :color="statusColor(detail.item.status)" />
                    <v-spacer />
                    <v-btn icon @click="detail.dialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text style="max-height: 68vh;">
                    <v-alert v-if="detail.message" dense text type="info" class="mt-4 mb-0">{{ detail.message }}</v-alert>

                    <div class="detail-section-title">Identificação</div>
                    <v-simple-table dense>
                        <tbody>
                            <tr><td class="detail-label">Destinatário</td><td>{{ detail.item.recipient }}</td></tr>
                            <tr><td class="detail-label">Nome</td><td>{{ detail.item.recipient_name || '—' }}</td></tr>
                            <tr><td class="detail-label">Assunto</td><td>{{ detail.item.subject || '—' }}</td></tr>
                            <tr><td class="detail-label">Tipo de e-mail</td><td>{{ detail.item.kind_label }}</td></tr>
                            <tr><td class="detail-label">Caso</td><td>{{ detail.item.client ? detail.item.client.name : '—' }}</td></tr>
                            <tr v-if="detail.item.user_id">
                                <td class="detail-label">Credor</td>
                                <td>
                                    <a href="javascript:;" @click="openUser(detail.item.user_id)">
                                        {{ detail.item.user ? detail.item.user.name : `Credor #${detail.item.user_id}` }}
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>

                    <div class="detail-section-title">Datas</div>
                    <v-simple-table dense>
                        <tbody>
                            <tr><td class="detail-label">Enviado em</td><td>{{ detail.item.sent_at | defaultDate("DD/MM/YYYY HH:mm:ss") }}</td></tr>
                            <tr v-if="detail.item.delivered_at"><td class="detail-label">Entregue em</td><td>{{ detail.item.delivered_at | defaultDate("DD/MM/YYYY HH:mm:ss") }}</td></tr>
                            <tr v-if="detail.item.failed_at"><td class="detail-label">Falhou em</td><td>{{ detail.item.failed_at | defaultDate("DD/MM/YYYY HH:mm:ss") }}</td></tr>
                            <tr v-if="detail.item.evidence_checked_at">
                                <td class="detail-label">Última consulta</td>
                                <td>
                                    {{ detail.item.evidence_checked_at | defaultDate("DD/MM/YYYY HH:mm") }}
                                    <span class="text-caption text--secondary ml-1">({{ sourceLabel(detail.item.evidence_source) }})</span>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>

                    <div class="detail-section-title">Confirmação do provedor</div>
                    <v-alert v-if="!detail.item.evidence" dense text type="warning" class="mb-0">
                        Ainda não há confirmação do provedor para este envio.
                        <template v-if="detail.item.status === 'error'">
                            O e-mail não chegou a ser aceito pelo Mailgun.
                        </template>
                        <template v-else>
                            Use "Consultar novamente" para verificar agora.
                        </template>
                    </v-alert>
                    <v-simple-table v-else dense>
                        <tbody>
                            <tr><td class="detail-label">Identificador da mensagem</td><td class="text-caption">{{ detail.item.provider_message_id || '—' }}</td></tr>
                            <tr><td class="detail-label">Servidor de destino</td><td>{{ detail.item.evidence.mx_host || '—' }}<span v-if="detail.item.evidence.mx_host_ip" class="text-caption text--secondary"> ({{ detail.item.evidence.mx_host_ip }})</span></td></tr>
                            <tr><td class="detail-label">Conexão segura (TLS)</td><td>{{ detail.item.evidence.tls === null || detail.item.evidence.tls === undefined ? '—' : (detail.item.evidence.tls ? 'Sim' : 'Não') }}</td></tr>
                            <tr><td class="detail-label">Código SMTP</td><td>{{ detail.item.evidence.smtp_code || '—' }}</td></tr>
                            <tr v-if="detail.item.evidence.smtp_message || detail.item.evidence.reason">
                                <td class="detail-label">Resposta do servidor</td>
                                <td class="text-caption">{{ detail.item.evidence.smtp_message || detail.item.evidence.reason }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>

                    <v-alert v-if="detail.item.error_message && !detail.item.evidence" dense text type="error" class="mt-3 mb-0">
                        {{ detail.item.error_message }}
                    </v-alert>

                    <p class="text-caption text--secondary mt-4 mb-0">
                        A confirmação de entrega é fornecida pelo provedor de envio e corresponde ao aceite do servidor
                        de destino. Não significa que o destinatário abriu o e-mail.
                    </p>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-btn text small :loading="detail.refreshing" @click="refreshDetail">
                        <v-icon left small>mdi-refresh</v-icon> Consultar novamente
                    </v-btn>
                    <v-spacer />
                    <v-btn color="primary" outlined small @click="downloadReceipt(detail.item)">
                        <v-icon left small>mdi-file-pdf-box</v-icon> Comprovante em PDF
                    </v-btn>
                    <v-btn text @click="detail.dialog = false">Fechar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import _ from "lodash";
import { downloadXlsx } from "@/common/util/export-xlsx.util";

export default {
    name: "EmailLogList",

    data: () => ({
        exportLoading: false,
        alert: { failures: 0, days: 7 },

        dataset: {
            clients: [],
            kinds: [],
            statuses: [],
        },

        detail: {
            dialog: false,
            item: null,
            refreshing: false,
            message: null,
        },

        dataList: {
            filter: {
                term: null,
                client_id: null,
                kind: null,
                status: null,
                start_date: null,
                end_date: null,
                user_id: null,
            },
            summary: { total: 0, delivered: 0, queued: 0, failed: 0, rejected: 0, error: 0 },
            headers: [
                { text: "Destinatário", value: "recipient" },
                { text: "Assunto", value: "subject" },
                { text: "Caso", value: "client" },
                { text: "Enviado em", value: "created_at" },
                { text: "Situação", value: "status" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            count: 0,
            options: { page: 1, itemsPerPage: 20 },
            loaded: { page: null, itemsPerPage: null },
            footerOptions: {
                itemsPerPageText: "E-mails por página:",
                itemsPerPageOptions: [10, 20, 50, 100],
                pageText: "{0}-{1} de {2}",
            },
        },
    }),

    created() {
        // "Ver todos" do cadastro do credor abre a tela ja filtrada por ele
        if (this.$route.query.user_id) this.dataList.filter.user_id = Number(this.$route.query.user_id);
        this.loadFilters();
        this.loadClients();
    },

    watch: {
        "dataList.options": {
            handler(value) {
                if (value.page === this.dataList.loaded.page && value.itemsPerPage === this.dataList.loaded.itemsPerPage) return;
                this.load();
            },
            deep: true,
        },
    },

    computed: {
        // Filtros escondidos em "Mais filtros" que estao preenchidos.
        moreFiltersCount() {
            return [this.dataList.filter.start_date, this.dataList.filter.end_date].filter((value) => value !== null && value !== undefined && value !== "" && value !== false).length;
        },
        hasFilters() {
            return Object.values(this.dataList.filter).some((value) => value);
        },

        // Cartoes do topo: clicar filtra pela situacao.
        summaryCards() {
            const s = this.dataList.summary;
            return [
                { key: null, label: "Total no filtro", value: s.total || 0, color: "primary" },
                { key: "delivered", label: "Entregues", value: s.delivered || 0, color: "success" },
                { key: "queued", label: "Na fila do provedor", value: s.queued || 0, color: "warning" },
                { key: "failed", label: "Não entregues", value: (s.failed || 0) + (s.rejected || 0) + (s.error || 0), color: "error" },
            ];
        },
    },

    methods: {
        applyFilters() {
            this.load(true);
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        // O watcher de options recarrega a lista.
        pageChanged(page) {
            this.dataList.options.page = page;
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.itemsPerPage = size;
        },

        statusColor(status) {
            return ({ delivered: "success", queued: "warning", failed: "error", rejected: "error", error: "grey darken-1" })[status] || "grey";
        },

        statusIcon(status) {
            return ({
                delivered: "mdi-check-circle-outline",
                queued: "mdi-clock-outline",
                failed: "mdi-alert-circle-outline",
                rejected: "mdi-cancel",
                error: "mdi-email-off-outline",
            })[status] || "mdi-email-outline";
        },

        sourceLabel(source) {
            return ({ events: "consulta ao provedor", webhook: "aviso do provedor", send: "envio" })[source] || source || "—";
        },

        buildSearch() {
            return Object.fromEntries(
                Object.entries(this.dataList.filter).filter(([, v]) => v !== null && v !== undefined && v !== "")
            );
        },

        async load(resetPage = false) {
            if (resetPage) this.dataList.options.page = 1;
            this.dataList.loading = true;
            try {
                const result = await axios.get("/api/admin/email-logs", {
                    params: {
                        page: this.dataList.options.page,
                        limit: this.dataList.options.itemsPerPage,
                        search: this.buildSearch(),
                    },
                });
                const content = result.data.content;
                this.dataList.data = content.data || [];
                this.dataList.count = content.paging ? content.paging.total : 0;
                this.dataList.summary = content.summary || this.dataList.summary;
                this.dataList.loaded = { page: this.dataList.options.page, itemsPerPage: this.dataList.options.itemsPerPage };
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        async loadFilters() {
            try {
                const result = await axios.get("/api/admin/email-logs/filters");
                const content = result.data.content;
                this.dataset.kinds = content.kinds || [];
                this.dataset.statuses = content.statuses || [];
                this.alert = content.alert || this.alert;
            } catch (error) {
                // silencioso: a tela funciona sem os filtros carregados
            } finally {
                this.load();
            }
        },

        async loadClients() {
            try {
                const result = await axios.get("/api/clients", { params: { start: 1, limit: 100000, search: {} } });
                const data = (result.data.content && result.data.content.data) || [];
                data.sort((a, b) => a.name.localeCompare(b.name));
                this.dataset.clients = data;
            } catch (error) {
                this.dataset.clients = [];
            }
        },

        toggleStatus(status) {
            this.dataList.filter.status = this.dataList.filter.status === status ? null : status;
            this.load(true);
        },

        showOnlyFailures() {
            this.dataList.filter.status = "failed";
            this.dataList.filter.start_date = this.$moment().subtract(this.alert.days, "days").format("YYYY-MM-DD");
            this.load(true);
        },

        clearFilters() {
            this.dataList.filter = { term: null, client_id: null, kind: null, status: null, start_date: null, end_date: null, user_id: null };
            this.applyFilters();
        },

        async openDetail(item) {
            this.detail.item = item;
            this.detail.message = null;
            this.detail.dialog = true;
            try {
                const result = await axios.get(`/api/admin/email-logs/${item.id}`);
                this.detail.item = result.data.content;
            } catch (error) {
                // mantem os dados da listagem
            }
        },

        async refreshDetail() {
            if (!this.detail.item) return;
            this.detail.refreshing = true;
            try {
                const result = await axios.post(`/api/admin/email-logs/${this.detail.item.id}/refresh`);
                const content = result.data.content;
                this.detail.message = content.message || null;
                this.detail.item = content;
                this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.detail.refreshing = false;
            }
        },

        openUser(userId) {
            this.$router.push(`/credores/${userId}`);
        },

        async downloadReceipt(item) {
            try {
                const result = await axios.get(`/api/admin/email-logs/${item.id}/receipt`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([result.data], { type: "application/pdf" }));
                window.open(url, "_blank");
                setTimeout(() => window.URL.revokeObjectURL(url), 60000);
            } catch (error) {
                this.$dialog.notify.error("Não foi possível gerar o comprovante.", { position: "top-right", timeout: 4000 });
            }
        },

        async exportXlsx() {
            this.exportLoading = true;
            try {
                const result = await axios.get("/api/admin/email-logs", { params: { all: 1, search: this.buildSearch() } });
                const items = (result.data.content && result.data.content.data) || [];
                if (!items.length) {
                    this.$dialog.notify.info("Nenhum e-mail para exportar com os filtros aplicados.", { position: "top-right", timeout: 3000 });
                    return;
                }
                const fmt = (value) => (value && value !== "0000-00-00 00:00:00" ? this.$options.filters.defaultDate(value, "DD/MM/YYYY HH:mm:ss") : "");
                const rows = items.map((item) => ({
                    "ID": item.id,
                    "DESTINATÁRIO": item.recipient,
                    "NOME": item.recipient_name || "",
                    "ASSUNTO": item.subject || "",
                    "TIPO": item.kind_label,
                    "CASO": item.client ? item.client.name : "",
                    "SITUAÇÃO": item.status_label,
                    "ENVIADO EM": fmt(item.sent_at || item.created_at),
                    "ENTREGUE EM": fmt(item.delivered_at),
                    "FALHOU EM": fmt(item.failed_at),
                    "CÓDIGO SMTP": item.evidence && item.evidence.smtp_code ? item.evidence.smtp_code : "",
                    "RESPOSTA DO SERVIDOR": (item.evidence && (item.evidence.smtp_message || item.evidence.reason)) || item.error_message || "",
                    "SERVIDOR DE DESTINO": item.evidence && item.evidence.mx_host ? item.evidence.mx_host : "",
                    "IDENTIFICADOR DA MENSAGEM": item.provider_message_id || "",
                }));
                await downloadXlsx(rows, `e-mails-${this.$moment().format("YYYY-MM-DD")}`, { sheetName: "E-mails" });
                this.$dialog.notify.success(`${rows.length} e-mail(s) exportado(s).`, { position: "top-right", timeout: 3000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || "Erro ao exportar", { position: "top-right", timeout: 5000 });
            } finally {
                this.exportLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.summary-card {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px 16px;
    cursor: pointer;
    transition: border-color 0.15s ease;
}

.summary-card:hover {
    border-color: #c7cbd1;
}

.summary-card--active {
    border-color: var(--v-primary-base);
    box-shadow: 0 0 0 1px var(--v-primary-base) inset;
}

.summary-value {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.1;
}

.summary-label {
    font-size: 12px;
    margin-top: 2px;
}

.detail-section-title {
    font-weight: 700;
    font-size: 13px;
    color: var(--v-primary-base);
    margin: 18px 0 6px;
}

.detail-label {
    width: 220px;
    color: #6b7280;
    font-size: 13px;
}
</style>
